import React from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import SecurityIcon from "@mui/icons-material/Security";
import moment from "moment";

import { cuotasFilter, cuotasOptions } from "./cuotas";

import {
  vigenciasSolicitudEmisionFilter,
  vigenciasSolicitudEmisionOptions,
} from "./vigencias";
import { useDispatch, useSelector } from "react-redux";
import {
  setVigenciaDesde,
  setNumeroPoliza,
  setPremioTotal,
  setSumaAsegurada,
  setCantidadCuotas,
  setVigencia,
} from "../actions";
import ItemNumericCard from "~business/Cards/ItemNumericCard";
import ItemCurrencyCard from "~business/Cards/ItemCurrencyCard";
import ItemSelectorCard from "~business/Cards/ItemSelectorCard";
import ItemDateCard from "~business/Cards/ItemDateCard";
import {
  CampoFormulario,
  CuotasFormulario,
  FechaFormulario,
} from "../../types";

const title = "Datos para la Póliza";

interface DatosPolizaCardProps {
  isLoading: boolean;
  displayError?: boolean;
  onBlur?: () => void;
}

interface SliceDatosPoliza {
  numeroPoliza: CampoFormulario<string | null>;
  premioTotal: CampoFormulario<string | number | null>;
  sumaAsegurada: CampoFormulario<string | number | null>;
  cantidadCuotas: CampoFormulario<number>;
  vigencia: CampoFormulario<number>;
  vigenciaDesde: CampoFormulario<FechaFormulario>;
  vigenciaHasta: CampoFormulario<FechaFormulario>;
  negocio?: string;
  cuotasSelected: CuotasFormulario;
  codigoOrganizador?: string;
  codigoProductor?: string;
  codigoVendedor?: string;
}

const useReduxSelector = () =>
  useSelector<any, SliceDatosPoliza>((state) => {
    const detalle = state.detalleSolicitudEmisionReducer;
    return {
      numeroPoliza: detalle.numeroPolizaSelected,
      premioTotal: detalle.premioTotalSelected,
      sumaAsegurada: detalle.sumaAseguradaSelected,
      cantidadCuotas: detalle.cantidadCuotasSelected,
      vigencia: detalle.vigenciaSelected,
      vigenciaDesde: detalle.vigenciaDesdeSelected,
      vigenciaHasta: detalle.vigenciaHastaSelected,
      negocio: detalle.detalleSolicitudEmision.negocio,
      cuotasSelected: detalle.cuotasSelected,
      codigoOrganizador: detalle.codigoOrganizadorSelected,
      codigoProductor: detalle.codigoProductorSelected,
      codigoVendedor: detalle.codigoVendedorSelected,
    };
  });

const DatosPolizaCard: React.FC<DatosPolizaCardProps> = ({
  isLoading,
  displayError,
  onBlur,
}) => {
  const dispatch = useDispatch();

  const {
    numeroPoliza,
    premioTotal,
    sumaAsegurada,
    cantidadCuotas,
    vigencia,
    vigenciaDesde,
    vigenciaHasta,
    negocio,
    cuotasSelected,
    codigoOrganizador,
    codigoProductor,
    codigoVendedor,
  } = useReduxSelector();

  let timer: ReturnType<typeof setTimeout> | undefined;
  const handleChangeNumeroPoliza = (nroPoliza: CampoFormulario<string>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setNumeroPoliza(nroPoliza));
    }, 200);
  };

  const handleChangePremioTotal = (premio: CampoFormulario<string | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setPremioTotal(premio));
    }, 200);
  };

  const handleChangeSumaAsegurada = (
    sumaAsegurada: CampoFormulario<string | null>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setSumaAsegurada(sumaAsegurada));
    }, 200);
  };

  const handleChangeCantidadCuotas = (
    cantidadCuotas: CampoFormulario<number>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setCantidadCuotas(cantidadCuotas));
    }, 200);
  };

  const handleChangeVigencia = (vigencia: CampoFormulario<number>) => {
    dispatch(setVigencia(vigencia));
  };

  const handleChangeVigenciaDesde = (
    vigenciaDesde: CampoFormulario<string | Date | null>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setVigenciaDesde(vigenciaDesde));
    }, 200);

    if (negocio === "Prendarios") {
      for (let i = 1; i <= 6; i++) {
        if (!cuotasSelected[i]) return;
        cuotasSelected[i].fechaVencimiento.value = moment(
          vigenciaDesde.value
        ).add(i, "months");
      }
    }
  };

  const isPrendarios = negocio === "Prendarios";
  return (
    <DetalleCard title={title} IconHeader={SecurityIcon}>
      <ItemNumericCard
        id={"nroPoliza"}
        isLoading={isLoading}
        label={"Número de Póliza"}
        value={numeroPoliza.value}
        onChange={handleChangeNumeroPoliza}
        onBlur={onBlur}
        focus={numeroPoliza.focus}
        helpText={"Ingrese solo números"}
      />
      <ItemCurrencyCard
        id={"premioTotal"}
        isLoading={isLoading}
        label={"Premio Total"}
        value={premioTotal.value as string | null}
        onChange={handleChangePremioTotal}
        onBlur={onBlur}
        displayError={displayError}
        focus={premioTotal.focus}
        required
      />
      <ItemCurrencyCard
        id={"sumaAsegurada"}
        isLoading={isLoading}
        label={"Suma Asegurada"}
        value={sumaAsegurada.value as string | null}
        onChange={handleChangeSumaAsegurada}
        onBlur={onBlur}
        displayError={displayError}
        focus={sumaAsegurada.focus}
        required
      />
      {!isPrendarios && (
        <ItemSelectorCard<number>
          id={"cantidadCuotas"}
          label={"Cantidad Cuotas"}
          isLoading={isLoading}
          value={cantidadCuotas.value}
          onChange={handleChangeCantidadCuotas}
          options={cuotasFilter}
          valueOptions={cuotasOptions}
          variant="big"
          required
        />
      )}
      {!isPrendarios && (
        <ItemSelectorCard<number>
          id={"vigencia"}
          isLoading={isLoading}
          label={"Vigencia"}
          value={vigencia.value}
          onChange={handleChangeVigencia}
          options={vigenciasSolicitudEmisionFilter}
          valueOptions={vigenciasSolicitudEmisionOptions}
          variant="big"
          required
        />
      )}
      <ItemDateCard
        id="vigenciaDesde"
        label="Vigencia Desde"
        isLoading={isLoading}
        value={vigenciaDesde.value as string | Date | null}
        onChange={handleChangeVigenciaDesde}
        onBlur={onBlur}
        displayError={displayError}
        focus={vigenciaDesde.focus}
      />
      <ItemDateCard
        id="vigenciaHasta"
        label="Vigencia Hasta"
        isLoading={isLoading}
        value={vigenciaHasta.value as string | Date | null}
        readOnly
      />
      {codigoOrganizador && (
        <ItemNumericCard
          label="Código organizador"
          isLoading={isLoading}
          value={codigoOrganizador}
          readOnly
        />
      )}
      {codigoProductor && (
        <ItemNumericCard
          label="Código productor"
          isLoading={isLoading}
          value={codigoProductor}
          readOnly
        />
      )}
      {codigoVendedor && (
        <ItemNumericCard
          label="Código vendedor"
          isLoading={isLoading}
          value={codigoVendedor}
          readOnly
        />
      )}
    </DetalleCard>
  );
};

export default DatosPolizaCard;
